import React, {useCallback, useEffect, useState} from 'react'
import {FiPlusCircle, FiStar, FiTrash, FiTriangle} from 'react-icons/fi'
import {useNavigate} from 'react-router'
import {Playlist} from '../common/types'
import {useLoginPrompt} from '../hooks/useLoginPrompt'
import {RenderPlaylist} from '../hooks/usePlaylists'
import {useSpotifyAuth} from '../hooks/useSpotifyAuth'
import {Api} from '../lib/api'
import styled, {useTheme} from '../lib/styled-components'
import {ToolTip} from './ToolTip'

const Wrapper = styled.div`
  margin: 10px;
`

const Content = styled.div`
  display: flex;
  background-color: ${p => p.theme.colors.background};
  border-radius: 10px;
  min-width: 380px;
  min-height: 80px;
`

const Side = styled.div`
  width: 30px;
  display: flex;
  padding-top: 10px;
  flex-direction: column;
  align-items: center;
  position: relative;
  .down {
    transform: scaleY(-1);
  }
  .svg {
    cursor: pointer;
  }
  gap: 20px;
`

type Size = 'small' | 'large'

const Embed = ({id, name, size}: {id: string; name: string; size?: Size}) => {
  //
  return (
    <iframe
      title={name}
      style={{marginLeft: '1px', transform: 'translateX(2px)'}}
      src={`https://open.spotify.com/embed/playlist/${id}`}
      width="100%"
      height={size === 'large' ? '380' : '80'}
      frameBorder="0"
      // allowTransparency={true}
      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      loading="lazy"
    ></iframe>
  )
}

const Select = ({
  onSelect,
  selected
}: {
  onSelect: (selected: boolean) => void
  selected: boolean
}) => {
  const theme = useTheme()
  return (
    <ToolTip label="Select two or more playlists to merge">
      <input
        style={{marginTop: '20px', borderColor: theme.colors.primary}}
        type="checkbox"
        checked={selected}
        onChange={e => onSelect(e.target.checked)}
      />
    </ToolTip>
  )
}

type Props = {
  playlist: RenderPlaylist
  size?: Size
  onSelect?: (playlist: RenderPlaylist, selected: boolean) => void
  selected?: boolean
}

export const PublicPlaylist = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      playlist,
      size,
      onSelect,
      selected
    }: {
      playlist: RenderPlaylist
      size?: Size
      onSelect?: (playlist: RenderPlaylist, selected: boolean) => void
      selected?: boolean
    },
    ref
  ) => {
    const [saved, setSaved] = useState<boolean>(playlist.isSaved)
    const [voted, setVoted] = useState<boolean>(playlist.isSaved)
    const [promptLogin, LoginPrompt] = useLoginPrompt()
    const [authState] = useSpotifyAuth()
    const theme = useTheme()

    useEffect(() => {
      setSaved(playlist.isSaved)
      setVoted(playlist.voted)
    }, [playlist.isSaved, playlist.voted])

    const save = async () => {
      if (!authState?.authenticated) {
        promptLogin()
      } else {
        const toggle = !saved
        setSaved(toggle)
        const res = toggle
          ? await Api.add(playlist.id)
          : await Api.remove(playlist.id)
        if (!res) {
          // revert
          setSaved(saved)
        }
      }
    }

    const vote = async () => {
      if (!authState?.authenticated) {
        promptLogin()
      } else {
        const toggle = !voted
        setVoted(toggle)
        const res = toggle
          ? await Api.voteUp(playlist.id)
          : await Api.voteDown(playlist.id)
        if (!res) {
          // revert
          setVoted(voted)
        }
      }
    }

    const userId = authState?.authenticated && authState.user.id

    return (
      <Wrapper id={playlist.id} ref={ref}>
        <Content style={{minHeight: size === 'large' ? '380px' : '80px'}}>
          <Side>
            <ToolTip label="Up vote">
              <VoteIcon
                onClick={vote}
                fill={voted ? theme.colors.primary : 'none'}
                stroke={theme.colors.primary}
              />
            </ToolTip>
            <ToolTip label="Favorite">
              <StarIcon
                fill={saved ? theme.colors.primary : 'none'}
                stroke={theme.colors.primary}
                onClick={save}
              />
            </ToolTip>
            {userId && playlist.added_by === userId}
            <ToolTip label="Remove">
              <RemoveIcon
                fill={'none'}
                stroke={theme.colors.primary}
                onClick={async () => {
                  await Api.removeFrontPage(playlist.id)
                }}
              />
            </ToolTip>
            {onSelect && (
              <Select
                selected={!!selected}
                onSelect={checked => onSelect(playlist, checked)}
              />
            )}
          </Side>
          <Embed id={playlist.id} name={playlist.name} size={size} />
        </Content>
        <LoginPrompt />
      </Wrapper>
    )
  }
)

const AddIcon = React.forwardRef<
  HTMLButtonElement,
  {onClick: () => Promise<void>; fill?: string; stroke?: string}
>(({fill, stroke, onClick}, ref) => {
  return (
    <span ref={ref}>
      <FiPlusCircle fill={fill} stroke={stroke} onClick={onClick} />
    </span>
  )
})

const VoteIcon = React.forwardRef<
  HTMLButtonElement,
  {onClick: () => Promise<void>; fill?: string; stroke?: string}
>(({fill, stroke, onClick}, ref) => {
  return (
    <span ref={ref}>
      <FiTriangle fill={fill} stroke={stroke} onClick={onClick} />
    </span>
  )
})

const StarIcon = React.forwardRef<
  HTMLButtonElement,
  {onClick: () => Promise<void>; fill?: string; stroke?: string}
>(({fill, stroke, onClick}, ref) => {
  return (
    <span ref={ref}>
      <FiStar fill={fill} stroke={stroke} onClick={onClick} />
    </span>
  )
})

const RemoveIcon = React.forwardRef<
  HTMLButtonElement,
  {onClick: () => Promise<void>; fill?: string; stroke?: string}
>(({fill, stroke, onClick}, ref) => {
  return (
    <span ref={ref}>
      <FiTrash fill={fill} stroke={stroke} onClick={onClick} />
    </span>
  )
})

export const MyPlaylist = ({
  playlist,
  size,
  selected,
  onSelect
}: {
  playlist: Playlist
  size?: Size
  selected?: boolean
  onSelect?: (playlist: Playlist, selected: boolean) => void
}) => {
  const addPlaylist = useCallback(
    (id: string) => Api.add(id, {frontPage: true}),
    []
  )
  const navigate = useNavigate()
  const theme = useTheme()

  return (
    <Wrapper id={playlist.id}>
      <Content>
        <Side>
          <ToolTip label="Add your playlist to the homepage">
            <AddIcon
              stroke={theme.colors.primary}
              fill={theme.colors.background}
              onClick={async () => {
                await addPlaylist(playlist.id)
                navigate('/')
              }}
            />
          </ToolTip>
          {onSelect && (
            <Select
              selected={!!selected}
              onSelect={checked => onSelect(playlist, checked)}
            />
          )}
        </Side>
        <Embed id={playlist.id} name={playlist.name} size={size} />
      </Content>
    </Wrapper>
  )
}
